import { useState } from "react";
import DOMPurify from "dompurify";
import { RichTextEditor } from "newui/components/ui/RichTextEditor/RichTextEditor";

interface SupplierCommentProps {
	/** Formik field name for this comment (e.g. "suppliers[0].comments"). */
	name: string;
	value: string | null;
	onChange: (html: string) => void;
	disabled?: boolean;
	supplierIndex: number;
}

/**
 * RTE-on-focus performance optimisation.
 *
 * react-quill is heavy. With 20+ suppliers we MUST NOT mount N RTEs at once.
 * The live RichTextEditor is mounted ONLY while this cell is focused
 * (`isFocused === true`). When it is not focused we render either a
 * lightweight clickable placeholder (no content) or a non-interactive
 * DOMPurify-sanitised preview (has content). This keeps exactly one RTE
 * mounted across the whole table, even when many suppliers already have
 * comments.
 */
export const SupplierComment = ({
	name,
	value,
	onChange,
	disabled = false,
	supplierIndex,
}: SupplierCommentProps) => {
	const [isFocused, setIsFocused] = useState(false);

	const hasContent = !!(value && value.trim() && value.trim() !== "<p><br></p>");

	// Locked / read-only: always a sanitised, non-interactive preview.
	if (disabled) {
		return (
			<div
				className="min-h-[60px] rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
				data-testid={`supplier-comment-readonly-${supplierIndex}`}
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{
					__html: DOMPurify.sanitize(value ?? ""),
				}}
			/>
		);
	}

	// Live editor mounted ONLY while focused — this is the perf gate.
	if (isFocused) {
		return (
			<div
				data-testid={`supplier-comment-rte-wrapper-${supplierIndex}`}
				onBlur={(e) => {
					// Quill moves focus internally (toolbar, editable area); only
					// collapse when focus actually leaves this cell entirely.
					if (!e.currentTarget.contains(e.relatedTarget)) {
						setIsFocused(false);
					}
				}}
			>
				<RichTextEditor
					value={value ?? ""}
					onChange={(html) => {
						onChange(html);
					}}
					testId={`supplier-comment-rte-${supplierIndex}`}
				/>
			</div>
		);
	}

	// Not focused, has content: non-interactive sanitised preview. Clicking it
	// (focus) swaps back to the live editor.
	if (hasContent) {
		return (
			<button
				type="button"
				className="block min-h-[60px] w-full rounded border border-gray-200 bg-white px-3 py-2 text-left text-sm text-gray-700 hover:border-gray-300"
				onFocus={() => setIsFocused(true)}
				onClick={() => setIsFocused(true)}
				data-testid={`supplier-comment-preview-${supplierIndex}`}
			>
				<span
					// eslint-disable-next-line react/no-danger
					dangerouslySetInnerHTML={{
						__html: DOMPurify.sanitize(value ?? ""),
					}}
				/>
			</button>
		);
	}

	// Not focused, empty: lightweight placeholder.
	return (
		<button
			type="button"
			className="block min-h-[60px] w-full cursor-text rounded border border-gray-200 bg-white px-3 py-2 text-left text-sm text-gray-500 hover:border-gray-300"
			onFocus={() => setIsFocused(true)}
			onClick={() => setIsFocused(true)}
			data-testid={`supplier-comment-placeholder-${supplierIndex}`}
		>
			Add comment…
		</button>
	);
};
